import { observer } from 'mobx-react-lite'
import { AI } from 'blaze-ui/icons'
import Styles from './BackgroundsDrawer.module.scss'
import { useRootStore } from 'stores/RootStore'
import { Camera1, Images3 } from 'blaze-ui/icons/20'
import { ImageButton } from './ImageButton'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { ImagePickerDrawer } from 'components/ImagePickerModal/ImagePickerDrawer/ImagePickerDrawer'
import { MediaFileClient } from 'api/clients/MediaFileClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { insertImage } from 'components/Design/UploadsTab/insertImage'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { Provider } from 'components/ImagePickerModal/Types'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useQueryClient } from 'react-query'
import { ACCEPTED_IMAGE_MIME_TYPES_NO_SVG } from 'screens/Finder/components/views/GalleryView/constants'
import { useUploadsTab } from 'components/Design/UploadsTab/UploadsTabContext'
import { createBackground } from '../createBackground'
import { useEditor } from 'components/VisualStudioPlayground/VisualEditorContext'

export const ImageButtons = observer(({ onSelectUploads }: { onSelectUploads: () => void }) => {
  const { drawersStore } = useRootStore()
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProject = useUploadsProject(workspaceId)?.data?.project
  const editor = useEditor()
  const queryClient = useQueryClient()
  const { forBackground } = useUploadsTab()
  const uploadsProjectQuery = useUploadsProject(workspaceId)
  const uploadsProjectId = uploadsProjectQuery.data?.project?.id

  const { fileInputRef, uploadImagesFromEvent } = useImportMediaFiles(false, (images) => {
    if (forBackground && images[0].width && images[0].height && images[0].source_url) {
      createBackground(editor, {
        src: images[0].source_url,
        w: images[0].width,
        h: images[0].height,
        mimeType: 'image/png',
      })

      drawersStore.closeDrawer()
    }

    if (uploadsProjectId) {
      queryClient.invalidateQueries(['workspace', workspaceId, 'files', uploadsProjectId])
    }
  })

  const openImageWizard = () => {
    if (!uploadsProject) return

    drawersStore.closeDrawer()

    drawersStore.openDrawer(ImagePickerDrawer, {
      hiddenProviders: new Set<Provider>(['stock', 'uploads', 'link', 'gifs']),
      defaultProvider: 'ai',
      maxCount: 1,
      uploadImages: async () => [],
      onConfirm: async (images) => {
        const image = images[0]
        if (!image?.id) return
        const response = await MediaFileClient.getAlmanacFile(image.id)
        if (response.type !== SUCCESS_RESPONSE_TYPE) {
          return
        }

        const file = response.data.almanac_file as IMediaFile

        if (forBackground && file.width && file.height) {
          createBackground(editor, {
            src: file.source_url,
            w: file.width,
            h: file.height,
            mimeType: 'image/png',
          })

          drawersStore.closeDrawer()
          return
        }

        insertImage(editor, file)
        drawersStore.closeDrawer()
      },
    })
  }

  return (
    <div className={Styles.mobileButtons}>
      <ImageButton icon={Camera1} onPress={() => fileInputRef.current?.click()}>
        Camera
      </ImageButton>
      <ImageButton icon={Images3} onPress={onSelectUploads}>
        Images
      </ImageButton>
      <ImageButton icon={AI} onPress={openImageWizard}>
        AI Image
      </ImageButton>
      <input
        className={Styles.fileInput}
        aria-label="Camera"
        ref={fileInputRef}
        type="file"
        onChange={(event) => {
          uploadImagesFromEvent(event, uploadsProjectId)
        }}
        accept={ACCEPTED_IMAGE_MIME_TYPES_NO_SVG}
        capture="environment"
      />
    </div>
  )
})
